import {
  defaultSeoTitle,
  liveCustomDomain,
  sameAsLinks,
  serviceAreas,
  type SeoCompany,
  type SeoCustomPage,
  type SeoPageRow,
  type SeoService,
} from './localSeo';

/**
 * The "Google & SEO" check: a short, plain-English checklist an owner can work
 * through. Pure so it can be unit-tested; the card only renders it.
 */

export type SeoCheckStatus = 'done' | 'partial' | 'todo' | 'locked';

export interface SeoCheckItem {
  id: string;
  title: string;
  /** Plain-English: what's good, or exactly what to do. */
  detail: string;
  status: SeoCheckStatus;
  href: string;
  /** Link label, e.g. "Add phone number". */
  action: string;
  weight: number;
}

export interface SeoCheckInput {
  company: (SeoCompany & { google_place_id?: string | null; google_reviews_enabled?: boolean | null }) | null;
  services: SeoService[];
  seoRows: SeoPageRow[];
  customPages: SeoCustomPage[];
  domains: Array<{ domain: string; verified_at: string | null; tls_status: string | null }>;
  /** null = unknown (treat as allowed). */
  customDomainAllowed: boolean | null;
}

/** Services with a description of at least this many characters count as "described". */
export const MIN_SERVICE_DESCRIPTION = 30;
/** Aim for at least this many described services. */
export const MIN_DESCRIBED_SERVICES = 3;

const EDITABLE_PAGES: Array<{ slug: string; label: string }> = [
  { slug: 'home', label: 'Homepage' },
  { slug: 'services', label: 'Services page' },
  { slug: 'about', label: 'About page' },
];

const has = (value: string | null | undefined) => Boolean(value && value.trim());

export function buildSeoChecklist(input: SeoCheckInput): { items: SeoCheckItem[]; score: number } {
  const company = input.company;
  const name = company?.company_name?.trim() ?? '';
  const areas = serviceAreas(company);
  const rows = new Map(input.seoRows.map((row) => [row.page_slug, row]));
  const items: SeoCheckItem[] = [];

  items.push({
    id: 'business-name',
    title: 'Business name',
    detail: has(name) ? `Google sees “${name}”.` : 'Add the name customers know you by — it goes in every Google title.',
    status: has(name) ? 'done' : 'todo',
    href: '/admin/company-info',
    action: 'Add business name',
    weight: 3,
  });

  items.push({
    id: 'phone',
    title: 'Phone number',
    detail: has(company?.phone_number)
      ? 'Your number is on your site and in your Google business details.'
      : 'Add a phone number so Google (and customers) can call you straight from search.',
    status: has(company?.phone_number) ? 'done' : 'todo',
    href: '/admin/company-info',
    action: 'Add phone number',
    weight: 2,
  });

  items.push({
    id: 'service-areas',
    title: 'Areas you cover',
    detail: areas.length
      ? `Covering ${areas.slice(0, 4).join(', ')}${areas.length > 4 ? '…' : ''}. These go into your titles so you show up for “cleaner in ${areas[0]}”.`
      : 'Add the towns you cover (e.g. Cardiff, Newport). People search “cleaner near me” and “cleaner in <town>”.',
    status: areas.length ? 'done' : 'todo',
    href: '/admin/company-info',
    action: 'Add service areas',
    weight: 3,
  });

  items.push({
    id: 'logo',
    title: 'Logo',
    detail: has(company?.logo_url)
      ? 'Your logo is used in Google results and when people share your site.'
      : 'Upload your logo — it appears in Google and on WhatsApp / Facebook link previews.',
    status: has(company?.logo_url) ? 'done' : 'todo',
    href: '/admin/company-info',
    action: 'Upload logo',
    weight: 1,
  });

  const described = input.services.filter((s) => (s.description ?? '').trim().length >= MIN_SERVICE_DESCRIPTION).length;
  items.push({
    id: 'services',
    title: `At least ${MIN_DESCRIBED_SERVICES} services with descriptions`,
    detail:
      described >= MIN_DESCRIBED_SERVICES
        ? `${described} services have a description. Nice — that's text Google can match to searches.`
        : input.services.length === 0
          ? 'Add the services you offer, each with a sentence or two about what’s included.'
          : `${described} of ${input.services.length} services have a proper description. Add a sentence or two about what’s included in each.`,
    status: described >= MIN_DESCRIBED_SERVICES ? 'done' : described > 0 ? 'partial' : 'todo',
    href: '/admin/jobs-pricing',
    action: 'Describe services',
    weight: 2,
  });

  for (const page of EDITABLE_PAGES) {
    const row = rows.get(page.slug);
    const hidden = row?.indexing_preference === 'noindex';
    const own = has(row?.seo_title) && has(row?.meta_description);
    const auto = defaultSeoTitle({ kind: page.slug as 'home' | 'services' | 'about' }, {
      businessName: name,
      company,
      services: input.services,
    });
    items.push({
      id: `page-${page.slug}`,
      title: `${page.label}: Google title & description`,
      detail: hidden
        ? `This page is set to “hide from Google”, so it won't appear in search. Switch it back to “index” unless that's deliberate.`
        : own
          ? 'You’ve written your own title and description.'
          : `We’re using an automatic title — “${auto}”. Writing your own description in your words helps you stand out.`,
      status: hidden ? 'todo' : own ? 'done' : 'partial',
      href: `/admin/page-builder?page=${page.slug}&seo=1`,
      action: hidden ? 'Fix visibility' : own ? 'Edit' : 'Write your own',
      weight: page.slug === 'home' ? 2 : 1,
    });
  }

  const publishedCustom = input.customPages.filter((p) => p.is_published !== false);
  if (publishedCustom.length) {
    const missing = publishedCustom.filter((p) => {
      const row = rows.get(p.slug);
      return !has(row?.meta_description) && !has(p.meta_description);
    });
    items.push({
      id: 'custom-pages',
      title: 'Your extra pages have descriptions',
      detail: missing.length
        ? `${missing.map((p) => p.title || p.slug).slice(0, 3).join(', ')}${missing.length > 3 ? '…' : ''} ${missing.length === 1 ? 'has' : 'have'} no description yet.`
        : 'Every published page has a description.',
      status: missing.length ? 'partial' : 'done',
      href: `/admin/page-builder?page=${(missing[0] ?? publishedCustom[0]).slug}&seo=1`,
      action: missing.length ? 'Add description' : 'Edit',
      weight: 1,
    });
  }

  const googleConnected = has(company?.google_place_id) && company?.google_reviews_enabled !== false;
  items.push({
    id: 'google-reviews',
    title: 'Google reviews connected',
    detail: googleConnected
      ? 'Your Google reviews show on your site. Keep asking happy customers to leave one.'
      : 'Connect your Google Business Profile so your reviews show on your site and customers can leave new ones.',
    status: googleConnected ? 'done' : 'todo',
    href: '/admin/reviews',
    action: 'Connect Google',
    weight: 2,
  });

  const social = sameAsLinks(company?.social_links);
  items.push({
    id: 'social',
    title: 'Social profiles linked',
    detail: social.length
      ? 'Your social profiles are linked, which helps Google connect them to your business.'
      : 'Link your Facebook or Instagram page — it helps Google confirm you’re a real local business.',
    status: social.length ? 'done' : 'todo',
    href: '/admin/company-info',
    action: 'Add social links',
    weight: 1,
  });

  const live = liveCustomDomain(input.domains);
  const pending = input.domains.length > 0 && !live;
  const locked = input.customDomainAllowed === false && !live && !pending;
  items.push({
    id: 'custom-domain',
    title: 'Your own web address',
    detail: live
      ? `Google indexes your site at ${live}.`
      : pending
        ? `${input.domains[0].domain} is still being connected. We'll switch Google over once it's live.`
        : locked
          ? 'Your own .co.uk / .com address builds trust and is yours to keep. Available on the Team plan.'
          : 'Use your own .co.uk / .com address — it builds trust and is yours to keep.',
    status: live ? 'done' : pending ? 'partial' : locked ? 'locked' : 'todo',
    href: locked ? '/admin/billing' : '/admin/domain',
    action: live ? 'Manage' : locked ? 'See plans' : pending ? 'Check status' : 'Connect domain',
    weight: 1,
  });

  const scored = items.filter((item) => item.status !== 'locked');
  const total = scored.reduce((sum, item) => sum + item.weight, 0);
  const earned = scored.reduce(
    (sum, item) => sum + (item.status === 'done' ? item.weight : item.status === 'partial' ? item.weight / 2 : 0),
    0,
  );
  return { items, score: total ? Math.round((earned / total) * 100) : 0 };
}
